import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { QRScanner, QRScanResult } from "../../components/QRScanner";
import "../../styles/QRScannerPage.css";
import { v4 as uuidv4 } from "uuid";

interface ItemDetails {
  productId: string;
  productName: string;
  balance: number;
  originalAmount: number;
  currency: string;
  status: "ACTIVE" | "REDEEMED" | "EXPIRED";
  issuedDate: string;
  expiryDate?: string;
  ownerId: string;
  ownerName?: string;
}

interface TransactionData {
  id: string;
  qrCode: string;
  itemDetails?: ItemDetails;
  scannedAt: Date;
  status: "pending" | "lookup" | "confirm" | "processing" | "success" | "error";
  message?: string;
  ledgerTxId?: string;
  newBalance?: number;
}

export function QRScannerPage() {
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const functions = getFunctions();

  const handleQRScan = async (result: QRScanResult) => {
    // Prevent duplicate scanning
    if (processingId) return;

    const qrCode = result.data;
    const transactionId = uuidv4();
    setProcessingId(transactionId);

    const transaction: TransactionData = {
      id: transactionId,
      qrCode,
      scannedAt: result.timestamp,
      status: "lookup",
      message: "Looking up item details...",
    };

    setTransactions((prev) => [transaction, ...prev]);

    try {
      // Step 1: Lookup QR code
      const qrLookup = httpsCallable<any, any>(functions, "qr_lookup");
      const lookupResult = await qrLookup({
        qrCode,
        scannedByUserId: "current-user", // Will be replaced with actual user ID
      });

      if (!lookupResult.data.success) {
        throw new Error(lookupResult.data.error || "Failed to lookup QR code");
      }

      const itemDetails = lookupResult.data.itemDetails;
      const walletItemId = lookupResult.data.walletItemId;

      // Update transaction with item details
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId
            ? {
                ...tx,
                itemDetails,
                status: "confirm",
                message: `Ready to process: ${itemDetails.productName} (${itemDetails.balance} ${itemDetails.currency})`,
              }
            : tx
        )
      );

      // Step 2: Process transaction (auto-process for now)
      setTimeout(async () => {
        try {
          setTransactions((prev) =>
            prev.map((tx) =>
              tx.id === transactionId
                ? { ...tx, status: "processing", message: "Processing transaction..." }
                : tx
            )
          );

          const qrProcess = httpsCallable<any, any>(functions, "qr_process_transaction");
          const processResult = await qrProcess({
            idempotencyKey: uuidv4(),
            walletItemId,
            amount: itemDetails.balance,
            transactionType: "REDEMPTION",
            notes: "QR code redemption",
          });

          if (processResult.data.success) {
            setTransactions((prev) =>
              prev.map((tx) =>
                tx.id === transactionId
                  ? {
                      ...tx,
                      status: "success",
                      message: "Transaction processed successfully",
                      ledgerTxId: processResult.data.ledgerTxId,
                      newBalance: processResult.data.newBalance,
                    }
                  : tx
              )
            );
          } else {
            throw new Error(processResult.data.error || "Processing failed");
          }
        } catch (error: any) {
          setTransactions((prev) =>
            prev.map((tx) =>
              tx.id === transactionId
                ? {
                    ...tx,
                    status: "error",
                    message: error.message || "Failed to process transaction",
                  }
                : tx
            )
          );
        }
      }, 2000);
    } catch (error: any) {
      setTransactions((prev) =>
        prev.map((tx) =>
          tx.id === transactionId
            ? {
                ...tx,
                status: "error",
                message: error.message || "Failed to lookup QR code",
              }
            : tx
        )
      );
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusIcon = (
    status: "pending" | "lookup" | "confirm" | "processing" | "success" | "error"
  ): string => {
    switch (status) {
      case "pending":
        return "⏳";
      case "lookup":
        return "🔍";
      case "confirm":
        return "⏸️";
      case "processing":
        return "⚙️";
      case "success":
        return "✓";
      case "error":
        return "✕";
      default:
        return "•";
    }
  };

  return (
    <div className="qr-scanner-page">
      <div className="page-header">
        <h1>📱 QR Code Scanner</h1>
        <p className="page-subtitle">
          Scan QR codes to process transactions instantly
        </p>
      </div>

      <div className="scanner-layout">
        {/* Scanner Section */}
        <div className="scanner-section">
          <div className="scanner-wrapper-box">
            <QRScanner onScan={handleQRScan} autoStart={true} showResult={true} />
          </div>

          <div className="scanner-hints">
            <h3>💡 Tips for scanning</h3>
            <ul>
              <li>Ensure good lighting for best results</li>
              <li>Position QR code in the center frame</li>
              <li>Keep camera steady for 1-2 seconds</li>
              <li>Use flashlight for dark environments</li>
            </ul>
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="history-section">
          <div className="history-header">
            <h3>📋 Transaction History</h3>
            <span className="transaction-count">
              {transactions.length} scanned
            </span>
          </div>

          {transactions.length === 0 ? (
            <div className="history-empty">
              <div className="empty-icon">🔍</div>
              <p>No transactions yet</p>
              <span className="empty-hint">Scan a QR code to get started</span>
            </div>
          ) : (
            <div className="transaction-list">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className={`transaction-item transaction-${tx.status}`}
                >
                  <div className="transaction-status">
                    <span className={`status-icon status-${tx.status}`}>
                      {getStatusIcon(tx.status)}
                    </span>
                  </div>

                  <div className="transaction-content">
                    <div className="transaction-header-row">
                      <div className="transaction-type">
                        {tx.itemDetails?.productName || "QR Code"}
                      </div>
                      <div className="transaction-time">
                        {tx.scannedAt.toLocaleTimeString()}
                      </div>
                    </div>

                    {tx.itemDetails && (
                      <>
                        <div className="transaction-details">
                          <span className="label">Owner:</span>
                          <span className="value">{tx.itemDetails.ownerName}</span>
                        </div>
                        <div className="transaction-details">
                          <span className="label">Amount:</span>
                          <span className="value">
                            {tx.itemDetails.balance} {tx.itemDetails.currency}
                          </span>
                        </div>
                        {tx.newBalance !== undefined && (
                          <div className="transaction-details">
                            <span className="label">New Balance:</span>
                            <span className="value">{tx.newBalance} {tx.itemDetails.currency}</span>
                          </div>
                        )}
                      </>
                    )}

                    <div className="transaction-id">
                      <span className="label">QR:</span>
                      <span className="id-text">{tx.qrCode.substring(0, 16)}...</span>
                    </div>

                    {tx.message && (
                      <div className="transaction-message">{tx.message}</div>
                    )}
                  </div>

                  <div className="transaction-actions">
                    {tx.status === "success" && (
                      <>
                        <button className="btn-action btn-view">👁️</button>
                        <button className="btn-action btn-share">📤</button>
                      </>
                    )}
                    {tx.status === "error" && (
                      <button className="btn-action btn-retry">🔄</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {transactions.length > 0 && (
        <div className="summary-stats">
          <div className="stat-card">
            <div className="stat-icon">✓</div>
            <div className="stat-info">
              <div className="stat-label">Successful</div>
              <div className="stat-value">
                {transactions.filter((t) => t.status === "success").length}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">✕</div>
            <div className="stat-info">
              <div className="stat-label">Failed</div>
              <div className="stat-value">
                {transactions.filter((t) => t.status === "error").length}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <div className="stat-label">Processing</div>
              <div className="stat-value">
                {transactions.filter((t) => t.status === "processing").length}
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-label">Total Processed</div>
              <div className="stat-value">
                {transactions
                  .filter((t) => t.status === "success")
                  .reduce((sum, t) => sum + (t.itemDetails?.balance || 0), 0)
                  .toFixed(2)}{" "}
                NOK
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

